/**
 * Which global filter axes cannot describe which tab — one declaration, both
 * surfaces (desktop FilterBar and mobile FilterModal).
 *
 * WHY THIS EXISTS. The filter bar is global: every control writes to the URL
 * and every tab inherits the value. But a tab's population is not always a
 * coach turn, and an axis that cannot describe a population must not pretend
 * to narrow it. Three separate per-pathname rules had grown for that one
 * question — granularity on /feedback, granularity on /jira, requestType on
 * /inbox — each written where it was noticed, none of them visible to the
 * mobile sheet, and a fourth was needed for /briefs. They are collapsed here.
 *
 * WHAT THE CONSUMER DOES WITH IT. It renders the control VISIBLY DISABLED and
 * attaches the reason, rather than unmounting it. Two reasons, in order:
 *
 *   1. Unmounting moves the page. The bar wraps; dropping three controls on
 *      /briefs collapses it from two rows to one, so the sticky chrome shrinks
 *      42px and every tab switch into or out of Briefs shoves the content up
 *      and back down (measured on the built bundle at 1920 and 1024; 1440 and
 *      1280 happened not to move). Disabling keeps the bar the same shape on
 *      every tab at every width, by construction.
 *   2. Unmounting removes the lie without telling the truth. The defect being
 *      fixed is a WRONG MENTAL MODEL — "I set Channel to Web, so these numbers
 *      are web-only". A control that vanishes teaches nothing and hides the
 *      fact that the inherited value is still set and still live on the tab
 *      the user came from. A greyed control still showing "Web", with a reason,
 *      says exactly what is true.
 *
 * THE URL IS UNTOUCHED EITHER WAY. `useUrlFilters` parses the URL and re-emits
 * it on every write, so a value set on one tab round-trips through a tab where
 * it does not apply. Disabling is the safer of the two on that front: the
 * control stays mounted and keeps rendering the inherited value.
 *
 * NOT AN OPT-OUT FOR THE SERVER. Making a control inert here does nothing to
 * a hand-typed URL — that is the job of the query layer
 * (`nonCoachTurnScopeSQL` in src/services/analytics/filters.ts, and the
 * `requestType` drop in src/dashboard/routes/api/inbox.ts). This file only
 * makes the UI honest about what the query layer already decided.
 */

/** The axes a route is allowed to declare inapplicable. */
export type FilterAxis = 'surface' | 'requestType' | 'inputMode' | 'granularity' | 'window' | 'dayFilter';

/** Axis → the short reason shown to the user, for one route. */
export type InapplicableAxes = Readonly<Partial<Record<FilterAxis, string>>>;

/**
 * Briefs, onboarding messages and mini-briefs are DELIVERED, not requested.
 * They carry no surface, no task-write marker and no input source, so all
 * three coach-turn axes are stripped from the brief queries
 * (`briefScopeSQL` → `nonCoachTurnScopeSQL`). Every other axis — role, cohort,
 * users, admins, days — describes the recipient and still applies.
 */
const BRIEF_POPULATION =
  'Briefs are delivered, not requested — they have no channel, request type or input mode. Other filters still apply.';

const INBOX_IS_ALL_CAPTURE =
  'Every Inbox row is a capture turn, so this cannot narrow the tab.';

const CAMPAIGN_IS_WHOLE = 'A campaign is shown whole, from its first send to its last press. Person filters still apply.';
const CAMPAIGNS_ARE_NOT_TURNS = 'Campaigns are deliveries and presses. This cannot narrow the tab; person filters still apply.';

const NO_TIME_SERIES = 'This tab is a list, not a time series.';

/** Contacts are cards and presses in a Slack DM, not coach turns (routes/api/contacts.ts drops these). */
const CONTACTS_ARE_NOT_TURNS =
  'Contacts are cards and presses in a Slack DM, not Mr. Edge\'s turns — this cannot narrow the tab. Other filters still apply.';

/**
 * Route prefix → inapplicable axes. FIRST MATCH WINS, so order the list
 * most-specific-first if prefixes ever overlap.
 *
 * Deliberately NOT listed:
 *   - /jira and /feedback keep surface / requestType / inputMode. Only PART of
 *     each tab opted out — the Jira CLICKS series and the Feedback
 *     `source=brief` list — while the Jira drafts numbers and the default
 *     `source=all` feedback list are coach turns that these axes really do
 *     narrow (see computeJiraUsageByType and computeFeedbackCards). Disabling
 *     the control there would break a working filter, which is worse than the
 *     affordance defect this file exists to fix.
 */
const INAPPLICABLE: ReadonlyArray<readonly [string, InapplicableAxes]> = [
  [
    '/briefs',
    {
      surface: BRIEF_POPULATION,
      requestType: BRIEF_POPULATION,
      inputMode: BRIEF_POPULATION,
    },
  ],
  ['/campaigns', { window: CAMPAIGN_IS_WHOLE, dayFilter: CAMPAIGN_IS_WHOLE, surface: CAMPAIGNS_ARE_NOT_TURNS,
    requestType: CAMPAIGNS_ARE_NOT_TURNS, inputMode: CAMPAIGNS_ARE_NOT_TURNS, granularity: NO_TIME_SERIES }],
  ['/inbox', { requestType: INBOX_IS_ALL_CAPTURE }],
  [
    '/contacts',
    { surface: CONTACTS_ARE_NOT_TURNS, requestType: CONTACTS_ARE_NOT_TURNS, inputMode: CONTACTS_ARE_NOT_TURNS },
  ],
  ['/feedback', { granularity: NO_TIME_SERIES }],
  ['/jira', { granularity: 'The Jira catalog is an inventory, not a time series.' }],
];

const NONE: InapplicableAxes = Object.freeze({});

/** The axes that do not apply on `pathname`, each with its reason. */
export function inapplicableAxes(pathname: string): InapplicableAxes {
  return INAPPLICABLE.find(([prefix]) => pathname.startsWith(prefix))?.[1] ?? NONE;
}
